import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FormHelperText } from '@mui/material';
import { ServiceCategory } from '../../types/service';
import { serviceCategoriesAPI } from '../../services/api';

// Define the service data interface for API
export interface ServiceData {
  service_id?: number;
  category_id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  discount_price?: number | null;
  image_url?: string | null;
  status: number; // 1 active, 0 inactive
  display_order: number;
}

// Define form values type (for form handling)
type ServiceFormValues = {
  category_id: string | number;
  name: string;
  description: string;
  duration: string | number;
  price: string | number;
  discount_price?: string | number;
  status: boolean;
  display_order: string | number;
};

// Validation schema
const serviceSchema: yup.ObjectSchema<ServiceFormValues> = yup
  .object({
    category_id: yup
      .mixed<string | number>()
      .test('is-integer', 'Category is required', (value) => !!value && Number(value) > 0)
      .required('Category is required'),
    name: yup.string().required('Service name is required'),
    description: yup.string().default(''),
    price: yup
      .mixed<string | number>()
      .test('is-number', 'Must be a number', (value) => !isNaN(Number(value)))
      .test('is-positive', 'Price must be positive', (value) => Number(value) > 0)
      .required('Price is required'),
    discount_price: yup
      .mixed<string | number>()
      .test('optional-number', 'Must be a number', (value) => value === undefined || value === '' || !isNaN(Number(value)))
      .optional(),
    duration: yup
      .mixed<string | number>()
      .test('is-number', 'Must be a number', (value) => !isNaN(Number(value)))
      .test('is-positive', 'Duration must be positive', (value) => Number(value) > 0)
      .test('is-integer', 'Must be a whole number', (value) => Number.isInteger(Number(value)))
      .required('Duration is required'),
    status: yup.boolean().default(true),
    display_order: yup
      .mixed<string | number>()
      .test('is-integer', 'Must be a whole number', (value) => {
        if (value === undefined || value === '') return true;
        return Number.isInteger(Number(value));
      })
      .default(0),
  })
  .required();

interface ServiceDetailsFormProps {
  onSuccess: (data: ServiceData) => Promise<void>;
  initialData?: Partial<ServiceData>;
  isSubmitting?: boolean;
}

const defaultValues: ServiceFormValues = {
  category_id: '',
  name: '',
  description: '',
  price: '',
  discount_price: '',
  duration: '30',
  status: true,
  display_order: '0',
};

const ServiceDetailsForm: React.FC<ServiceDetailsFormProps> = ({
  onSuccess,
  initialData = {},
  isSubmitting = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ServiceFormValues>({
    resolver: yupResolver(serviceSchema) as any, // Type assertion to handle yup and react-hook-form types
    defaultValues: { 
      ...defaultValues, 
      ...(initialData ? {
        category_id: initialData.category_id?.toString() || '',
        name: initialData.name || '',
        description: initialData.description || '',
        duration: initialData.duration?.toString() || '30',
        price: initialData.price?.toString() || '',
        discount_price: initialData.discount_price?.toString() || '',
        status: initialData.status !== undefined ? initialData.status === 1 : true,
        display_order: initialData.display_order?.toString() || '0',
      } : {})
    } as ServiceFormValues,
  });

  // Load categories on component mount
useEffect(() => {
  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      console.log("🔍 Loading service categories...");
      const categories = await serviceCategoriesAPI.getAll({ status: 1 });

      setCategories(categories);
      console.log("✅ Loaded categories:", categories);
    } catch (error) {
      console.error("❌ Error loading categories:", error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  loadCategories();
}, []);

  // Update form values when initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      console.log('🔄 Updating form with new initialData:', initialData);
      reset({
        category_id: initialData.category_id?.toString() || '',
        name: initialData.name || '',
        description: initialData.description || '',
        duration: initialData.duration?.toString() || '30',
        price: initialData.price?.toString() || '',
        discount_price: initialData.discount_price?.toString() || '',
        status: initialData.status !== undefined ? initialData.status === 1 : true,
        display_order: initialData.display_order?.toString() || '0',
      } as ServiceFormValues);
    }
  }, [initialData, reset]);

  const onSubmit: SubmitHandler<ServiceFormValues> = async (data) => {
    try {
      setIsLoading(true);
      // Convert string values to numbers for the API
      const formattedData: ServiceData = {
        category_id: Number(data.category_id),
        name: data.name,
        description: data.description || '',
        duration: Number(data.duration) || 0,
        price: Number(data.price) || 0,
        discount_price: data.discount_price === '' || data.discount_price === undefined ? null : Number(data.discount_price),
        image_url: null,
        status: data.status ? 1 : 0,
        display_order: data.display_order ? Number(data.display_order) : 0,
      };
      
      await onSuccess(formattedData);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Service Information
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Service Name"
                variant="outlined"
                error={!!errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="category_id"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth variant="outlined" error={!!errors.category_id}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  {...field}
                  labelId="category-label"
                  label="Category"
                  disabled={loadingCategories}
                >
                  {loadingCategories ? (
                    <MenuItem value="" disabled>
                      Loading categories...
                    </MenuItem>
                  ) : categories.length > 0 ? (
                    categories.map((category) => (
                      <MenuItem key={category.category_id} value={category.category_id.toString()}>
                        {category.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No categories available
                    </MenuItem>
                  )}
                </Select>
                <FormHelperText error={!!errors.category_id}>
                  {errors.category_id?.message as string}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Price ($)"
                type="number"
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                error={!!errors.price}
                helperText={errors.price?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="discount_price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Discount Price ($)"
                type="number"
                variant="outlined"
                inputProps={{ min: 0, step: '0.01' }}
                error={!!errors.discount_price}
                helperText={errors.discount_price ? (errors.discount_price.message as string) : ''}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Duration (minutes)"
                type="number"
                variant="outlined"
                inputProps={{ min: 1 }}
                error={!!errors.duration}
                helperText={errors.duration?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller<ServiceFormValues, 'description'>
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Description"
                multiline
                rows={4}
                variant="outlined"
                error={!!errors.description}
                helperText={errors.description?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Controller<ServiceFormValues, 'display_order'>
            name="display_order"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Display Order"
                type="number"
                variant="outlined"
                inputProps={{ min: 0 }}
                error={!!errors.display_order}
                helperText={errors.display_order?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller<ServiceFormValues, 'status'>
            name="status"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    color="primary"
                  />
                }
                label="Active Service"
                labelPlacement="start"
                sx={{ justifyContent: 'space-between', ml: 0, mr: 2, width: '100%' }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || isSubmitting}
              startIcon={(isLoading || isSubmitting) ? <CircularProgress size={20} /> : null}
            >
              {isLoading || isSubmitting ? 'Saving...' : 'Save & Continue'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default ServiceDetailsForm;
